import { ensureDefined } from "common/utils/assertOfType";
import type { MultiPolygon, Geom } from "polygon-clipping";
import polygonClipping from "polygon-clipping";
import { aabb2AddPoint, aabb2ByPolyLine2s, aabb2Grow, aabb2New, aabb2Union } from "../aabb2";
import type { Face } from "../brep";
import { DIST_EPSILON } from "../core";
import { planeNewXY } from "../plane";
import { vec2Dist, vec2Scale } from "../vec2";
import type { Polygon, Segment } from "./dcel";
import type { DCEL } from "./dcel";
import { dcelFromPolygons, dcelFromPolygonsSnapRound, DEFAULT_SNAP_ROUND_PRECISION } from "./dcelFromPolygons";
import { polyIntersect, polySubtract, polyUnion } from "./setOps";
import type { FacePlaneIntersection } from "./setOps3";
import type { SetOpsTestCase } from "./test/setOpsTestCaseType";
import { polygonToFace } from "./trimFaceWithPlane";
import { vec2Normal, vec2Lerp, vec2Tangent } from "./vec2";

interface RenderSetOps3TestCaseIntoCanvasProps {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  result: FacePlaneIntersection[];
  face: Face;
  scale: number;
  dcel: DCEL | "NO_ISECT";
  showDCELFaces?: boolean;
}

export function renderSetOps3TestCaseIntoCanvas({
  canvas,
  ctx,
  result,
  face,
  dcel,
  showDCELFaces,
  scale: viewerScale,
}: RenderSetOps3TestCaseIntoCanvasProps) {
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.save();

  // get the bounding box of the face in its plane
  let aabb = aabb2ByPolyLine2s(face.loops);

  if (result.length > 0 && aabb) {
    for (const res of result) {
      if (res.type === "TRANSVERSE") {
        for (const segment of res.segments) {
          aabb2AddPoint(aabb, segment[0], segment[1]);
          aabb2AddPoint(aabb, segment[2], segment[3]);
        }
      } else if (res.type === "COPLANAR") {
        const aabbOppFace = aabb2ByPolyLine2s(res.oppFace.loops);
        aabb = aabbOppFace ? aabb2Union(aabb, aabbOppFace) : aabb;
      }
    }
  } else if (!aabb) {
    aabb = aabb2New(-DIST_EPSILON, -DIST_EPSILON, DIST_EPSILON, DIST_EPSILON);
  }

  const PADDING = 5;
  aabb2Grow(aabb, PADDING, PADDING);

  const { minX, minY, maxX, maxY } = aabb;
  const width = maxX - minX;
  const height = maxY - minY;

  const scale = Math.min(canvas.width / width, canvas.height / height);

  const ratio = window.devicePixelRatio;
  const ss = scale / ratio;
  ctx.translate(-ss * minX, canvas.height / ratio + ss * minY);
  ctx.scale(ss, -ss);

  ctx.lineWidth = 1;
  ctx.strokeStyle = "black";
  ctx.fillStyle = "rgba(0, 0, 0, 0.3)";

  // draw all of the loops

  function drawFace(face: Face) {
    for (const loop of face.loops) {
      ctx.beginPath();

      for (let i = 0; i < loop.length; i += 2) {
        const x = loop[i];
        const y = loop[i + 1];

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }

      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
  }

  function drawArrowOnPath(ax: number, ay: number, bx: number, by: number) {
    const [tx, ty] = vec2Tangent(ax, ay, bx, by);
    const [nx, ny] = vec2Normal(ax, ay, bx, by);

    const arrowLength = 6;
    const arrowWidth = 4;

    ctx.save();
    ctx.fillStyle = "black";
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(bx - tx * arrowLength + (nx * arrowWidth) / 2, by - ty * arrowLength + (ny * arrowWidth) / 2);
    ctx.lineTo(bx - tx * arrowLength - (nx * arrowWidth) / 2, by - ty * arrowLength - (ny * arrowWidth) / 2);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  ctx.save();
  ctx.lineWidth = 1 / scale;

  if (result.length > 0) {
    for (const res of result) {
      if (res.type === "TRANSVERSE") {
        for (const segment of res.segments) {
          ctx.beginPath();
          ctx.moveTo(segment[0], segment[1]);
          ctx.lineTo(segment[2], segment[3]);
          ctx.stroke();
          // draw directional arrow following the direction of the segment
          drawArrowOnPath(segment[2], segment[3], segment[0], segment[1]);
        }
      } else if (res.type === "COPLANAR") {
        drawFace(res.oppFace);
      }
    }
  }
  ctx.restore();

  drawFace(face);

  if (showDCELFaces && dcel && dcel !== "NO_ISECT") {
    for (let i = 0; i < dcel.faceHalfEdges.length; i++) {
      if (dcel.faceIsInterior(i)) {
        const poly = dcel.buildPolygonFromFace(i);
        const face = polygonToFace(poly, planeNewXY());
        ctx.save();
        ctx.strokeStyle = "red";
        ctx.fillStyle = `hsl(${i * 30}, 100%, 50%)`;
        drawFace(face);
        ctx.restore();
      }
    }

    const fontSize = 12 / scale;

    renderDCEL(ctx, dcel, 0.5 / viewerScale);
    renderDCELEdges(ctx, dcel, 3, 0.25 / viewerScale, Math.min(6, fontSize / viewerScale));
    renderDCELVerts(ctx, dcel, Math.min(6, fontSize / viewerScale));
  }

  ctx.restore();
}

export function renderSetOpsTestCaseIntoCanvas(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  test: SetOpsTestCase,
  viewerScale: number,
  renderInput = true,
  renderResult = true,
  renderSnapRoundResult = true,
  renderPolygonClippingResult = true,
) {
  ctx.save();
  ctx.fillStyle = "white";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.restore();

  ctx.save();
  const { polygons, op, operands } = test;

  const min = [Infinity, Infinity];
  const max = [-Infinity, -Infinity];

  // get the bounding box of all polygons and scale the canvas to fit
  for (let i = 0; i < polygons.length; i++) {
    const polygonLoops = polygons[i];
    for (let j = 0; j < polygonLoops.length; j++) {
      for (const point of polygonLoops[j]) {
        if (point[0] < min[0]) {
          min[0] = point[0];
        }
        if (point[1] < min[1]) {
          min[1] = point[1];
        }
        if (point[0] > max[0]) {
          max[0] = point[0];
        }
        if (point[1] > max[1]) {
          max[1] = point[1];
        }
      }
    }
  }

  const PADDING = 9;

  min[0] -= PADDING;
  min[1] -= PADDING;
  max[0] += PADDING;
  max[1] += PADDING;

  const width = max[0] - min[0];
  const height = max[1] - min[1];

  const scale = Math.min(canvas.width / width, canvas.height / height);

  const ratio = window.devicePixelRatio;
  const ss = scale / ratio;
  ctx.translate(-ss * min[0], canvas.height / ratio + ss * min[1]);
  ctx.scale(ss, -ss);

  ctx.lineWidth = 2 / (scale * viewerScale);

  if (renderInput) {
    if (test.displayInputOnly) {
      renderPolygons(ctx, polygons, false, true);
      ctx.restore();
      return;
    } else {
      renderPolygons(ctx, polygons);
    }
  }

  console.time(`polyclip (snap round): ${test.name}`);

  const dcelSnapRound = dcelFromPolygonsSnapRound({ polygons, snapRoundPrecision: DEFAULT_SNAP_ROUND_PRECISION });

  let resultPolygonsSnapRound: Polygon[] | undefined;

  if (op === "intersect") {
    resultPolygonsSnapRound = polyIntersect(dcelSnapRound, operands[0]);
  } else if (op === "union") {
    resultPolygonsSnapRound = polyUnion(dcelSnapRound, operands[0]);
  } else if (op === "subtract") {
    resultPolygonsSnapRound = polySubtract(dcelSnapRound, operands[0], operands[1]);
  }

  console.timeEnd(`polyclip (snap round): ${test.name}`);

  console.time(`polyclip: ${test.name}`);

  const dcel = dcelFromPolygons({ polygons });

  let resultPolygons: Polygon[] | undefined;

  if (op === "intersect") {
    resultPolygons = polyIntersect(dcel, operands[0]);
  } else if (op === "union") {
    resultPolygons = polyUnion(dcel, operands[0]);
  } else if (op === "subtract") {
    resultPolygons = polySubtract(dcel, operands[0], operands[1]);
  }

  console.timeEnd(`polyclip: ${test.name}`);

  let res: polygonClipping.MultiPolygon | undefined;
  console.time(`polygon-clipping: ${test.name}`);

  if (op === "intersect") {
    res = polygonClipping.intersection(
      test.polygons[operands[0][0]],
      ...(operands[0].slice(1).map((x) => test.polygons[x]) as Geom[]),
    );
  } else if (op === "union") {
    res = polygonClipping.union(
      test.polygons[operands[0][0]],
      ...(operands[0].slice(1).map((x) => test.polygons[x]) as Geom[]),
    );
  } else if (op === "subtract") {
    res = polygonClipping.difference(
      test.polygons[operands[0][0]],
      ...(operands[1].map((x) => test.polygons[x]) as Geom[]),
    );
  }

  console.timeEnd(`polygon-clipping: ${test.name}`);

  if (test.debug) {
    let [isRenderDCEL, isRenderDCELEdges, isRenderDCELVerts] = [true, true, true];
    if (typeof test.debug !== "boolean") {
      isRenderDCEL = test.debug[0] ?? false;
      isRenderDCELEdges = test.debug[1] ?? false;
      isRenderDCELVerts = test.debug[2] ?? false;
    }
    isRenderDCEL ? renderDCEL(ctx, dcel) : null;
    isRenderDCELEdges ? renderDCELEdges(ctx, dcel, scale) : null;
    isRenderDCELVerts ? renderDCELVerts(ctx, dcel, scale) : null;
    ctx.restore();
  }

  if (renderResult) {
    ctx.strokeStyle = "#ff0000";
    ctx.fillStyle = "rgba(255, 0, 0, 0.2)";
    renderPolygons(ctx, ensureDefined(resultPolygons), true, true);
  }

  if (renderSnapRoundResult) {
    ctx.strokeStyle = "#0000ff";
    ctx.fillStyle = "rgba(0, 0, 255, 0.2)";
    renderPolygons(ctx, ensureDefined(resultPolygonsSnapRound), true, true);
  }

  if (renderPolygonClippingResult && res) {
    ctx.strokeStyle = "#00ff00";
    renderMultiPolygon(ctx, res, false);
  }

  ctx.restore();
}

export function renderPolygons(
  ctx: CanvasRenderingContext2D,
  polygons: Polygon[],
  fill = false,
  strokeStyleRandomizer = false,
) {
  for (const i of Object.keys(polygons)) {
    const polygon = polygons[i];
    ctx.beginPath();

    for (const j in polygon) {
      const loop = polygon[j];
      let hue = (+i * 13 + +j * 7) % 360;
      if (strokeStyleRandomizer) {
        hue = Math.random() * 360;
      }
      ctx.strokeStyle = `hsla(${hue}, 100%, 50%, 1)`;
      const start = loop[loop.length - 1];
      ctx.moveTo(start[0], start[1]);
      for (const point of loop) {
        ctx.lineTo(point[0], point[1]);
      }
    }
    ctx.closePath();
    ctx.stroke();
    if (fill) {
      ctx.fill();
    }
  }
}

export function renderMultiPolygon(ctx: CanvasRenderingContext2D, polygons: MultiPolygon, fill = false) {
  for (const polygon of polygons) {
    ctx.beginPath();
    for (const loop of polygon) {
      const start = loop[loop.length - 1];
      ctx.moveTo(start[0], start[1]);
      for (const point of loop) {
        ctx.lineTo(point[0], point[1]);
      }
      ctx.closePath();
    }
    if (fill) {
      ctx.fill();
    }
    ctx.stroke();
  }
}

export function renderDCEL(ctx: CanvasRenderingContext2D, dcel: DCEL, scale: number = 1) {
  ctx.save();

  ctx.lineWidth = 0.5 * scale;
  ctx.strokeStyle = `white`;

  for (let parentFaceId = 0; parentFaceId < dcel.faceHalfEdges.length; parentFaceId++) {
    // simple PRNG for face color
    ctx.fillStyle = `hsla(${Math.abs(Math.sin(parentFaceId) * 10000)}, 50%, 50%, 1)`;

    if (!dcel.faceIsInterior(parentFaceId)) {
      continue;
    }

    const polygon = dcel.buildPolygonFromFace(parentFaceId);

    ctx.beginPath();

    for (const loop of polygon) {
      const start = loop[loop.length - 1];
      ctx.moveTo(start[0], start[1]);

      for (const point of loop) {
        ctx.lineTo(point[0], point[1]);
      }

      ctx.closePath();
    }

    ctx.fill();
  }
  ctx.restore();
}

function renderSegment(
  ctx: CanvasRenderingContext2D,
  segment: Segment,
  scale: number = 1,
  addArrow: boolean = false,
  linewidth = 1,
) {
  ctx.save();
  ctx.lineWidth = linewidth * scale;
  ctx.beginPath();

  ctx.moveTo(segment[0], segment[1]);
  ctx.lineTo(segment[2], segment[3]);

  ctx.stroke();

  if (addArrow) {
    const arrowSize = 0.2 * scale;
    const [nx, ny] = vec2Normal(...segment);

    const contractedSegment = contractSegment(segment, -arrowSize);

    ctx.beginPath();

    ctx.moveTo(segment[2], segment[3]);
    ctx.lineTo(contractedSegment[2] + (nx * arrowSize) / 2, contractedSegment[3] + (ny * arrowSize) / 2);
    ctx.lineTo(contractedSegment[2] - (nx * arrowSize) / 2, contractedSegment[3] - (ny * arrowSize) / 2);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  ctx.restore();
}

const DCEL_SEGMENT_OFFSET = 1;
const DCEL_VERTEX_SEGMENT_RADIUS = 2;

export function renderDCELEdges(
  ctx: CanvasRenderingContext2D,
  dcel: DCEL,
  scale: number = 1,
  linewidth: number = 1,
  fontsize = 3,
  extraContent: ((halfEdgeIndex: number) => string) | undefined = undefined,
) {
  for (let i = 0; i < dcel.halfEdges.length; i++) {
    const segment: Segment = [...dcel.vertices[dcel.halfEdges[i][0]], ...dcel.vertices[dcel.halfEdges[i][1]]];

    const dist = vec2Dist(...segment);

    ctx.strokeStyle = "black";
    ctx.fillStyle = "black";

    renderSegment(ctx, segment, scale, false, linewidth);
    renderSegment(
      ctx,
      contractSegment(offsetSegment(segment, DCEL_SEGMENT_OFFSET * scale), -3 * DCEL_SEGMENT_OFFSET * scale),
      scale,
      true,
      linewidth / 2,
    );

    const dx = segment[2] - segment[0];
    const dy = segment[3] - segment[1];

    let rot = 0;
    if (dist > 0) {
      if (dx === 0) {
        rot = Math.PI / 2;
      } else {
        rot = Math.atan(dy / dx);
      }
    }

    const [nx, ny] = vec2Normal(...dcel.vertices[dcel.halfEdges[i][0]], ...dcel.vertices[dcel.halfEdges[i][1]]);
    const [xx, yy] = vec2Lerp(...dcel.vertices[dcel.halfEdges[i][0]], ...dcel.vertices[dcel.halfEdges[i][1]], 0.5);

    const facePolys = dcel.faceToPolygons[dcel.halfEdgeToFace[i]];

    let arrayFacePolys: number[] = [];
    if (facePolys) {
      arrayFacePolys = Array.from(facePolys);
    }

    const [textOffsetX, textOffsetY] = vec2Scale(nx, ny, fontsize * scale + DCEL_SEGMENT_OFFSET * scale);

    ctx.save();

    ctx.fillStyle = "black";
    renderText(
      ctx,
      `${i}-${dcel.halfEdgeToFace[i]}-${dcel.halfEdgeToPolygon[i]}-[${arrayFacePolys}]${
        extraContent ? `(${extraContent(i)})` : ""
      }`,
      xx + textOffsetX,
      yy + textOffsetY,
      fontsize,
      scale,
      rot,
    );

    ctx.restore();
  }
}

export function renderDCELVerts(ctx: CanvasRenderingContext2D, dcel: DCEL, scale: number = 1) {
  for (let i = 0; i < dcel.vertices.length; i++) {
    const [x, y] = dcel.vertices[i];

    // move to vertex position, assign scale, and add content
    ctx.save();

    ctx.translate(x, y);
    ctx.scale(scale, scale);

    ctx.fillStyle = "green";

    ctx.beginPath();
    ctx.arc(0, 0, DCEL_VERTEX_SEGMENT_RADIUS, 0, 2 * Math.PI);
    ctx.fill();

    ctx.fillStyle = "white";

    renderText(ctx, i.toString(), 0, 0, 1.25 * DCEL_VERTEX_SEGMENT_RADIUS, 1);

    ctx.restore();
  }
}

function renderText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  size: number = 8,
  scale: number = 1,
  rot: number = 0,
) {
  ctx.save();

  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.scale(scale, -scale);

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const scaledFontSize = size;
  ctx.font = `${scaledFontSize}px sans-serif`;

  ctx.fillText(text, 0, 0);
  ctx.restore();
}

function offsetSegment(segment: Segment, dist: number): Segment {
  const [nx, ny] = vec2Normal(segment[0], segment[1], segment[2], segment[3]);

  return [segment[0] + nx * dist, segment[1] + ny * dist, segment[2] + nx * dist, segment[3] + ny * dist];
}

function contractSegment(segment: Segment, distStart: number, distEnd: number = distStart): Segment {
  const [tx, ty] = vec2Tangent(segment[0], segment[1], segment[2], segment[3]);

  return [
    segment[0] - tx * distStart,
    segment[1] - ty * distStart,
    segment[2] + tx * distEnd,
    segment[3] + ty * distEnd,
  ];
}
